import { useState, useRef, useCallback } from "react";
import toast from "react-hot-toast";
import { SERVER_URL } from "../../../utils/serverConfig";

// Shared SSE streaming hook for AI endpoints (work-summary + org-intelligence).
// Both stream the same event shape: data: {type, text, ...}.
// Returns { text, visualizations, streaming, cached, error, limitReached, run, stop, reset }.
export function useAiStream(endpoint) {
  const [text, setText] = useState("");
  // Progress line from the server ({type:"status"}). Previously dropped on the
  // floor, which is why a long analysis showed a static placeholder.
  const [status, setStatus] = useState(null);
  const [visualizations, setVisualizations] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [report, setReport] = useState(null);
  const [reportPartial, setReportPartial] = useState(false); // true while report is still streaming in
  const [streaming, setStreaming] = useState(false);
  const [cached, setCached] = useState(false);
  const [error, setError] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const abortRef = useRef(null);

  const reset = useCallback(() => {
    setText(""); setVisualizations([]); setMetrics(null); setReport(null); setReportPartial(false); setError(null); setCached(false); setStatus(null);
  }, []);

  const stop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setStreaming(false);
  }, []);

  const run = useCallback(async (body) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setStreaming(true); setError(null); setCached(false);
    setText(""); setVisualizations([]); setMetrics(null); setReport(null); setReportPartial(false); setLimitReached(false); setStatus(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${SERVER_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
        signal: abortRef.current.signal,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        if (errData?.error?.code === "AI_LIMIT_REACHED") { setLimitReached(true); setStreaming(false); return; }
        throw new Error(`Request failed: ${res.statusText}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const d = JSON.parse(line.substring(6));
            if (d.type === "metrics") {
              // Generic: forward the whole metrics payload (minus `type`) so BOTH
              // daily (totalHours/sessionCount/…) and hourly (activeMinutes/
              // segmentCount/appBreakdown/…) endpoints flow through untouched.
              const { type, ...m } = d;
              setMetrics({ breakdownKind: "project", topApps: [], topSites: [], copyFlows: [], ...m });
            }
            else if (d.type === "report") {
              // Progressive: partial reports arrive repeatedly as the AI writes;
              // each is a superset of the last, so last-wins. `partial` flag drives
              // the shimmer / "still writing" affordance in the renderer.
              setReport(d.report);
              setReportPartial(d.partial === true);
            }
            else if (d.type === "status") { setStatus({ message: d.message, detail: d.detail || null, step: d.step || null, steps: d.steps || null }); }
            else if (d.type === "cached") { setCached(true); }
            else if (d.type === "content") { setText((prev) => prev + d.text); }
            // The agentic chat endpoint (/chat/stream) emits {type:"text", content}.
            else if (d.type === "text") { setText((prev) => prev + (d.content || "")); }
            else if (d.type === "visualization") {
              // Support BOTH the nested (data.visualization.*) and flat shapes,
              // and dedupe identical charts — matching the legacy pages exactly.
              const v = d.visualization || {};
              const vizData = {
                chartType: v.chartType || d.chartType,
                title: v.title || d.title,
                data: v.data || d.data,
                config: v.config || d.config || {},
                columns: v.columns || d.columns,
                nameKey: v.nameKey || d.nameKey,
                valueKey: v.valueKey || d.valueKey,
                xKey: v.xKey || d.xKey,
                yKey: v.yKey || d.yKey,
              };
              setVisualizations((prev) => {
                const exists = prev.some((p) =>
                  p.chartType === vizData.chartType &&
                  p.title === vizData.title &&
                  JSON.stringify(p.data) === JSON.stringify(vizData.data)
                );
                return exists ? prev : [...prev, vizData];
              });
            }
            else if (d.type === "no_activity") { setText(""); setError(d.message); setStreaming(false); toast(d.message, { icon: "ℹ️" }); }
            else if (d.type === "error") { setError(d.message); toast.error(d.message); }
            else if (d.type === "done") { setStreaming(false); setStatus(null); window.dispatchEvent(new Event("ai-credit-used")); }
          } catch (_) { /* ignore parse errors */ }
        }
      }
      setStreaming(false);
    } catch (err) {
      if (err.name !== "AbortError") { setError(err.message); toast.error(err.message); }
      setStreaming(false);
    }
  }, [endpoint]);

  return { text, visualizations, metrics, report, reportPartial, streaming, cached, error, limitReached, setLimitReached, status, run, stop, reset };
}
